import { createSelector } from '@ngrx/store';
import { DataListState } from './data-list.state';

export const ANNOUNCEMENTS = 'announcements';
export const CHATS = 'chats';
export const CHECK_INS = 'checkIns';
export const CHECK_IN_TAGS = 'checkInTags';
export const CUSTOMER_TEAM = 'customerTeam';
export const CUSTOMER_LISTS = 'customerLists';
export const CUSTOMERS = 'customers';
export const FEED = 'feed';
export const FEEDBACK = 'feedback';
export const MARKET_MANAGEMENT_CATEGORIES = 'marketManagementCategories';
export const MEMO_CATEGORIES = 'memoCategories';
export const MEMORIAMS = 'memoriams';
export const MEMOS = 'memos';
export const NOTIFICATIONS = 'notifications';
export const ORGANISATIONS = 'organisations';
export const ORGANISATION_BRANDING = 'organisationBranding';
export const REQUESTS = 'requests';
export const MEMBERS = 'members';
export const STAFF_CUSTOMERS = 'staffCustomers';
export const TENANTS = 'tenants';
export const QUESTIONS = 'questions';
export const MEMBER_BADGES = 'memberBadges';

export type Segment =
  | typeof ANNOUNCEMENTS
  | typeof CHATS
  | typeof CHECK_INS
  | typeof CHECK_IN_TAGS
  | typeof CUSTOMERS
  | typeof CUSTOMER_TEAM
  | typeof CUSTOMER_LISTS
  | typeof FEED
  | typeof FEEDBACK
  | typeof MARKET_MANAGEMENT_CATEGORIES
  | typeof MEMO_CATEGORIES
  | typeof MEMORIAMS
  | typeof MEMOS
  | typeof NOTIFICATIONS
  | typeof ORGANISATIONS
  | typeof ORGANISATION_BRANDING
  | typeof REQUESTS
  | typeof MEMBERS
  | typeof STAFF_CUSTOMERS
  | typeof TENANTS
  | typeof QUESTIONS
  | typeof MEMBER_BADGES;

export const stateSegment = (segment: Segment) => (superState: any) => superState[segment];

const reducers: any = {};
[
  ANNOUNCEMENTS,
  CHATS,
  CHECK_INS,
  CHECK_IN_TAGS,
  CUSTOMERS,
  CUSTOMER_TEAM,
  CUSTOMER_LISTS,
  FEED,
  FEEDBACK,
  MARKET_MANAGEMENT_CATEGORIES,
  MEMBER_BADGES,
  MEMO_CATEGORIES,
  MEMORIAMS,
  MEMOS,
  NOTIFICATIONS,
  ORGANISATIONS,
  ORGANISATION_BRANDING,
  QUESTIONS,
  REQUESTS,
  STAFF_CUSTOMERS,
  MEMBERS,
  TENANTS,
].forEach((selector) => {
  reducers[selector] = {};
  reducers[selector].getIds = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.ids
  );
  reducers[selector].getEntities = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.entities
  );
  reducers[selector].getAll = createSelector(
    reducers[selector].getEntities,
    reducers[selector].getIds,
    (entities, ids: Array<any>) => ids.map((id) => entities[id])
  );
  reducers[selector].getSelectedId = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.selectedId
  );
  reducers[selector].getSelected = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.entities[state.selectedId]
  );
  reducers[selector].getError = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.error
  );
  reducers[selector].getLoadingEntities = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.loadingEntities
  );
  reducers[selector].getSelectedIsLoading = createSelector(
    reducers[selector].getLoadingEntities,
    reducers[selector].getSelectedId,
    (loadingEntities, id: string) => !!loadingEntities[id]
  );
  reducers[selector].getLoadingAll = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.loadingAll
  );
  reducers[selector].getCreating = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.creating
  );
  /// Material Datatable pagination
  reducers[selector].getPagination = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.page
  );
  /// Old School CareApp Pagination
  reducers[selector].getPaginators = createSelector(
    stateSegment.call(this, selector),
    (state: any) => state.paginators
  );
  reducers[selector].getFilter = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.filter
  );
  reducers[selector].getSearching = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.searching
  );
  reducers[selector].getSearchResults = createSelector(
    stateSegment.call(this, selector),
    (state: DataListState<any>) => state.searchResults.map((id) => state.entities[id])
  );
  reducers[selector].getUpdatedAt = createSelector(
    stateSegment.call(this, selector),
    (state: any) => state.updatedAt
  );
});

export const getAll = (segment: Segment) => reducers[segment].getAll;
export const getCreating = (segment: Segment) => reducers[segment].getCreating;
export const getEntities = (segment: Segment) => reducers[segment].getEntities;
export const getError = (segment: Segment) => reducers[segment].getError;
export const getFilter = (segment: Segment) => reducers[segment].getFilter;
export const getIds = (segment: Segment) => reducers[segment].getIds;
export const getLoadingAll = (segment: Segment) => reducers[segment].getLoadingAll;
export const isLoading = getLoadingAll;
export const getLoadingEntities = (segment: Segment) => reducers[segment].getLoadingEntities;
export const getSelectedIsLoading = (segment: Segment) => reducers[segment].getSelectedIsLoading;
export const getPageination = (segment: Segment) => reducers[segment].getPagination;
export const getSearching = (segment: Segment) => reducers[segment].getSearching;
export const getSearchResults = (segment: Segment) => reducers[segment].getSearchResults;
export const getSelected = (segment: Segment) => reducers[segment].getSelected;
export const getSelectedId = (segment: Segment) => reducers[segment].getSelectedId;

export const getPaginators = (segment: Segment) => reducers[segment].getPaginators;

// Caching selectors ensures we can mock them for testing in `provideMockStore`
const allPaginatedCache = {};
const paginatedCompleteCache = {};
const paginatedLoadingCache = {};
const paginatedErrorCache = {};

export const getAllPaginated = (segment: Segment, id: string = segment) => {
  const cache = allPaginatedCache;
  if (cache[id + segment]) {
    return cache[id + segment];
  }
  cache[id + segment] = createSelector(
    getPaginators(segment),
    getEntities(segment),
    (paginators = {}, entities) => {
      const paginator = paginators[id];
      const paginatedIds = paginator && paginator.ids ? paginator.ids : [];
      return paginatedIds.map((id) => entities[id]).filter((entity) => !!entity);
    }
  );
  return cache[id + segment];
};

export const getUpdatedAt = (segment: Segment) => reducers[segment].getUpdatedAt;
export const getAllPaginatedComplete = (segment: Segment, id: string = segment) => {
  const cache = paginatedCompleteCache;
  if (cache[id + segment]) {
    return cache[id + segment];
  }
  cache[id + segment] = createSelector(getPaginators(segment), (paginators) =>
    paginators && paginators[id] ? paginators[id].complete : false
  );
  return cache[id + segment];
};

export const isPaginatedLoading = (segment: Segment, id: string = segment) => {
  const cache = paginatedLoadingCache;
  if (cache[id + segment]) {
    return cache[id + segment];
  }
  cache[id + segment] = createSelector(getPaginators(segment), (paginators) =>
    paginators[id] ? paginators[id].loading : false
  );
  return cache[id + segment];
};

export const isPaginatedError = (segment: Segment, id: string = segment) => {
  const cache = paginatedErrorCache;
  if (cache[id + segment]) {
    return cache[id + segment];
  }
  cache[id + segment] = createSelector(getPaginators(segment), (paginators) =>
    paginators[id] ? paginators[id].error : false
  );
  return cache[id + segment];
};
